/**
 * The run dialog's state: the fields, the notes and transcripts (uploaded through `POST /api/files`, each one's text
 * status asked again every 2 s while it is read), and the save: a new run, or a revision of `source`.
 *
 * Run waits while a file is uploading or its text is still being read, so the needs call sees every file. There is
 * no route to take an upload back: a file the run never uses is removed by the server's sweep after a day.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { FILE_STATUS_MAX, UPLOAD_TOO_LARGE, useFileStatuses, useRerunAdvisorRun, useStartAdvisorRun, useUploadCatalogFile } from '@/api/queries';
import { AdvisorRerunIn, RunIn, runInFileIdsMax } from '@/api/generated/schemas';
import type { AdvisorIdeaRef, AdvisorRun, AdvisorRunIn, CatalogFileOut, TextStatus } from '@/api/types';
import { fieldErrors, serverFieldErrors } from '@/api/validate';
import { useAttachmentDraft, type AttachmentRules } from '@/ui/Attachments';

export const RUN_FILE_RULES: AttachmentRules = {
  maxFiles: Math.min(runInFileIdsMax, FILE_STATUS_MAX),
  maxBytes: 50 * 1024 * 1024,
  accept:
    '.pdf,.docx,.pptx,.md,.markdown,.txt,application/pdf,text/markdown,text/plain,' +
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document,' +
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  acceptedNames: /\.(pdf|docx|pptx|md|markdown|txt)$/i,
  acceptedTypes: [
    'application/pdf',
    'text/markdown',
    'text/plain',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ],
  hint: 'Or drop files here. PDF, Word, PowerPoint, Markdown or text, up to 50 MB each, ten at a time.',
  tooLarge: UPLOAD_TOO_LARGE,
  wrongType: 'Not a PDF, Word, PowerPoint, Markdown or text file',
  screenshots: false,
};

type RunFile = CatalogFileOut;

/** What a person types, checked as a new run or as a refinement takes it; the ids come from the server. */
const TYPED_NEW = RunIn.pick({ client: true, problem: true, crm_reference: true });
const TYPED_RERUN = AdvisorRerunIn.pick({ client: true, problem: true, crm_reference: true });

/** The source run's files as the picker's kept rows (their text was read when that run started). */
function keptFiles(source: AdvisorRun | undefined): RunFile[] {
  return (source?.files ?? []).map((f) => ({
    id: f.id,
    filename: f.filename,
    content_type: f.content_type,
    size_bytes: f.size_bytes,
    text_status: f.text_status,
    reused: true,
  }));
}

function useRunFiles(source: AdvisorRun | undefined) {
  const keep = useMutation({ meta: { quiet: true }, mutationFn: async (_id: string) => undefined });
  const draft = useAttachmentDraft<RunFile>({ rules: RUN_FILE_RULES, upload: useUploadCatalogFile(), remove: keep, initial: keptFiles(source) });
  const pending = draft.files.filter((f) => f.text_status === 'pending').map((f) => f.id);
  const statuses = useFileStatuses(pending);
  const statusOf = (file: RunFile): TextStatus => statuses.data?.find((s) => s.id === file.id)?.text_status ?? file.text_status;
  const reading = draft.files.some((f) => statusOf(f) === 'pending');
  return { draft, statusOf, reading };
}

interface StartValues {
  client: string;
  problem: string;
  industryId: string;
  country: string;
  linked: AdvisorIdeaRef | null;
  crm: string;
}

/** Blank for a new run (with `idea` linked when the dialog opened from one), the run's own inputs for a revision. */
export function startValues(source: AdvisorRun | undefined, idea: AdvisorIdeaRef | null): StartValues {
  if (!source) return { client: '', problem: '', industryId: '', country: '', linked: idea, crm: '' };
  return {
    client: source.client,
    problem: source.problem,
    industryId: source.industry?.id ?? '',
    country: source.country ?? '',
    linked: source.idea,
    crm: source.crm_reference ?? '',
  };
}

export function useRunFields(source: AdvisorRun | undefined, idea: AdvisorIdeaRef | null) {
  const [start] = useState(() => startValues(source, idea));
  const [client, setClient] = useState(start.client);
  const [problem, setProblem] = useState(start.problem);
  const [industryId, setIndustryId] = useState(start.industryId);
  const [country, setCountry] = useState(start.country);
  const [linked, setLinked] = useState<AdvisorIdeaRef | null>(start.linked);
  const [crm, setCrm] = useState(start.crm);
  return { client, setClient, problem, setProblem, industryId, setIndustryId, country, setCountry, linked, setLinked, crm, setCrm };
}

/** What the request sends: the trimmed fields, empty ones as null. A revision keeps a hidden opportunity as it was. */
export function runBody(fields: ReturnType<typeof useRunFields>, fileIds: string[], source?: AdvisorRun): AdvisorRunIn {
  const body: AdvisorRunIn = {
    client: fields.client.trim(),
    problem: fields.problem.trim(),
    industry_id: fields.industryId || null,
    country: fields.country || null,
    file_ids: fileIds,
    idea_id: fields.linked?.id ?? null,
    crm_reference: fields.crm.trim() || null,
  };
  if (source && source.idea_linked && !source.idea) delete body.idea_id;
  return body;
}

export function useRunForm(source: AdvisorRun | undefined, idea: AdvisorIdeaRef | null, onStarted: (run: AdvisorRun) => void) {
  const fields = useRunFields(source, idea);
  const files = useRunFiles(source);
  const start = useStartAdvisorRun();
  const rerun = useRerunAdvisorRun(source?.id ?? '');
  const save = source ? rerun : start;
  const typed = { client: fields.client, problem: fields.problem, crm_reference: fields.crm };
  const invalid = Object.keys(fieldErrors(source ? TYPED_RERUN : TYPED_NEW, typed)).length > 0;
  const canSubmit = !invalid && !files.draft.busy && !files.reading && !save.isPending;

  function onSubmit() {
    if (!canSubmit) return;
    save.mutate(runBody(fields, files.draft.ids, source), {
      onSuccess: (run) => {
        files.draft.sent();
        onStarted(run);
      },
    });
  }

  /** A 422's messages by field path, shown beside their fields: the typed ones; the banner carries the rest. */
  const serverErrors = serverFieldErrors(save.error);
  const placed = Object.keys(typed);
  return { ...fields, files, save, invalid, canSubmit, onSubmit, serverErrors, placed, revising: Boolean(source) };
}

export type RunForm = ReturnType<typeof useRunForm>;
